import React, { useState } from 'react';
import { Card, Form, Input, Button, Typography, Flex, theme, List, Grid, Alert } from 'antd';
import { WhatsAppOutlined, CheckOutlined } from '@ant-design/icons';
import { WIDGET_DATA } from '../../../constants';
import { assistantService } from '../../../../assistant-management/services/assistantManagementService';
import { WhatsAppConfigValues } from '../../../types';

const { Text, Paragraph } = Typography;
const { useToken } = theme;
const { useBreakpoint } = Grid;

// Tipos
interface WhatsAppConfigProps {
  chatbotId: string;
  chatbotData: any;
}

// Componente para el encabezado de WhatsApp
const WhatsAppHeader: React.FC<{ 
  item: typeof WIDGET_DATA[1]; 
  isMobile: boolean; 
  token: any 
}> = ({ item, isMobile, token }) => (
  <List.Item>
    <Flex
      justify="space-between"
      align={isMobile ? 'flex-start' : 'center'}
      style={{ width: '100%' }}
      vertical={isMobile}
      gap={isMobile ? token.marginSM : 0}
    >
      <Flex
        align="start"
        gap={token.marginMD}
        style={{ width: isMobile ? '100%' : 'auto' }}
      >
        {item.iconType === 'whatsapp' && <WhatsAppOutlined style={item.iconStyle} />}
        <Flex vertical gap={'8px'}>
          <Text strong>{item.title}</Text>
          <Paragraph type="secondary" style={{ margin: 0 }}>
            {item.description}
          </Paragraph>
          <Text type="secondary">Setup: {item.setup}</Text>
        </Flex>
      </Flex>
      {/* <Button style={{ width: isMobile ? '100%' : 'auto' }}>
        {item.action}
      </Button> */}
    </Flex>
  </List.Item>
);

// Componente para el formulario de configuración
const WhatsAppConfigForm: React.FC<{
  form: any;
  onFinish: (values: WhatsAppConfigValues) => Promise<void>;
  loading: boolean;
}> = ({ form, onFinish, loading }) => (
  <Form
    form={form}
    layout="vertical"
    onFinish={onFinish}
    style={{ maxWidth: '900px', width: '70%', margin: '0 auto' }}
  >
    <Alert
      type="info"
      showIcon
      style={{ marginBottom: 16 }}
      message="Existing values are not displayed for security reasons"
      description="The fields below appear empty even if a value is already saved. Leave a field empty to keep its current value; complete a field only to replace it."
    />
    <Form.Item name="phoneNumber" label="Phone Number">
      <Input placeholder="Leave empty to keep current value" />
    </Form.Item>
    <Form.Item name="accessToken" label="Access Token">
      <Input.Password placeholder="Leave empty to keep current value" />
    </Form.Item>
    <Form.Item name="phoneNumberId" label="Phone Number ID">
      <Input placeholder="Leave empty to keep current value" />
    </Form.Item>
    <Form.Item name="verifyToken" label="Verify Token">
      <Input.Password placeholder="Leave empty to keep current value" />
    </Form.Item>
    <Form.Item name="webhookUrl" label="Webhook URL">
      <Input placeholder="Leave empty to keep current value" />
    </Form.Item>
    <Form.Item name="whatsappBusinessAccountId" label="WhatsApp Business Account ID">
      <Input placeholder="Leave empty to keep current value" />
    </Form.Item>

    <Form.Item>
      <Button 
        type="primary" 
        icon={<CheckOutlined />} 
        htmlType="submit"
        loading={loading}
      >
        Save
      </Button>
    </Form.Item>
  </Form>
);

// Componente principal
export default function MyAssistantWhatsAppConfig({ chatbotId, chatbotData }: WhatsAppConfigProps) {
  const { token } = useToken();
  const [form] = Form.useForm<WhatsAppConfigValues>();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [loading, setLoading] = useState(false);

  const handleSave = async (values: WhatsAppConfigValues) => {
    // Only send fields the user actually filled in. Empty fields are treated
    // as "keep the current value" by the backend.
    const partialUpdate = Object.fromEntries(
      Object.entries(values).filter(([, value]) => typeof value === 'string' && value.trim() !== '')
    ) as Partial<WhatsAppConfigValues>;

    if (Object.keys(partialUpdate).length === 0) {
      return;
    }

    try {
      setLoading(true);
      await assistantService.updateWhatsAppConfig({ chatbotId, whatsappConfig: partialUpdate as WhatsAppConfigValues });
      form.resetFields();
    } catch (err) {
      // Error is already handled by the service
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card style={{ maxWidth: 1200, margin: 'auto', border: 'none' }}>
      <Flex vertical gap={token.marginLG}>
        <List
          itemLayout="horizontal"
          dataSource={[WIDGET_DATA[1]]}
          renderItem={(item) => (
            <WhatsAppHeader 
              item={item} 
              isMobile={isMobile} 
              token={token} 
            />
          )}
        />
        <WhatsAppConfigForm 
          form={form} 
          onFinish={handleSave} 
          loading={loading} 
        />
      </Flex>
    </Card>
  );
}
